import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { DisplayPnlEvent } from '@flipster/utils/segment/events/display-pnl';

import { pnlModeBunja, type DisplayedPnlType } from './state';

interface Props {
  onBack?: () => void;
  onClose: () => void;
}

const PnlSettingContent = ({ onClose, onBack }: Props) => {
  const t = useTranslations();
  const { pnlModeAtom } = useBunja(pnlModeBunja);
  const [pnlMode, setPnlMode] = useAtom(pnlModeAtom);

  const defaultPnlMode = useMemo(
    () => (onBack ? pnlMode : pnlMode === 'unrealizedPnl' ? 'netPnl' : 'unrealizedPnl'),
    [onBack, pnlMode],
  );

  const [selected, setSelected] = useState<DisplayedPnlType>(defaultPnlMode);

  const onChange = (value: DisplayedPnlType) => {
    setSelected(value);
  };

  const onClickConfirm = () => {
    setPnlMode(selected);
    segment.track(DisplayPnlEvent.DisplayPnlSettingsConfirmClick, {
      display_pnl_type: pnlTypeLabelStringMap[selected],
    });
    snackbar.success({
      message: <FormattedMessage id='Autogen_common.r251126.s_Your_P_L_display_setting_has_been_updated' />,
    });
    onClose();
  };

  return (
    <>
      <Modal.Content>
        <fieldset
          className={stack({
            gap: 10,
            mb: 10,
          })}
        >
          {pnlDisplayItems.map((item) => (
            <div
              key={item.value}
              className={css({
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: selected === item.value ? 'border.default.level-5' : 'border.default.level-1',
                px: 20,
                py: 16,
                rounded: 12,
              })}
              onClick={() => onChange(item.value as DisplayedPnlType)}
            >
              <RadioButton
                value={item.value}
                checked={selected === item.value}
                onChange={(e) => onChange(e.currentTarget.value as DisplayedPnlType)}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 6,
                  textStyle: 'Text/L/Regular',
                  color: 'content.default.level-5',
                }}
              >
                {item.label}
              </RadioButton>
              <p className={defaultDescCss}>{item.desc}</p>
            </div>
          ))}
        </fieldset>
        <div className={hstack({ color: 'content.default.level-4', textStyle: 'Body/S/Regular', gap: 8 })}>
          <InformationCircleIcon size={16} color={token('colors.content.default.level-4')} />
          <FormattedMessage id='Autogen_common.r251126.s_This_setting_will_be_applied_to_all_open_positions' />
        </div>
      </Modal.Content>
      <Modal.Footer>
        {onBack && (
          <Modal.Button
            hierarchy='secondary'
            size='large'
            color='neutral'
            label={t('Autogen_common.r251126.s_back')}
            onClick={onBack}
          />
        )}
        <Modal.Button
          hierarchy='primary'
          size='large'
          color='accent'
          label={t('Autogen_common.r251126.s_Confirm')}
          onClick={onClickConfirm}
          disabled={selected === pnlMode}
        />
      </Modal.Footer>
    </>
  );
};

export default PnlSettingContent;

const pnlTypeLabelStringMap: Record<DisplayedPnlType, 'Unrealized P&L' | 'Net P&L'> = {
  unrealizedPnl: 'Unrealized P&L',
  netPnl: 'Net P&L',
};

const pnlDisplayItems = [
  {
    label: <FormattedMessage id='Assets.r250401.Unrealized_pnl' />,
    value: 'unrealizedPnl',
    desc: <FormattedMessage id='Autogen_common.r251126.s_Profit_or_loss_before_fees_and_realized_gains_losses' />,
  },
  {
    label: <FormattedMessage id='Autogen_common.r251126.s_Net_PnL' />,
    value: 'netPnl',
    desc: <FormattedMessage id='Autogen_common.r251126.s_Profit_or_loss_after_fees_and_realized_gains_losses' />,
  },
];

const defaultDescCss = css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' });
